import { memo } from "react";
import { useTranslation } from "react-i18next";
import type { DeviceAccount } from "../modal/deviceAccounts";
import DeviceAccountList from "../modal/DeviceAccountList";
import { CardIcon, LockIcon } from "../modal/deviceModalIcons";
import {
  DeviceSectionCard,
  DeviceSectionHeader,
  EYEBROW_CLASS,
} from "../modal/deviceModalParts";

interface Props {
  accounts: DeviceAccount[];
  selectedIds: number[];
  onSelectedChange: (ids: number[]) => void;
  isNarrow: boolean;
}

/**
 * Секцията на терминала за плащане — кои работни точки и потребители са
 * закачени към него. Акаунт на друг терминал се вижда, но не се избира.
 */
const TerminalAccountsSection = ({ accounts, selectedIds, onSelectedChange, isNarrow }: Props) => {
  const { t } = useTranslation();
  const selectedSet = new Set(selectedIds);
  const chosenCount = accounts.filter(
    (account) => account.takenBy === null && selectedSet.has(account.id),
  ).length;
  const takenAccounts = accounts.filter((account) => account.takenBy !== null);
  const takenTerminals = [...new Set(takenAccounts.map((account) => account.takenBy))];

  return (
    <DeviceSectionCard>
      <DeviceSectionHeader
        icon={<CardIcon size={18} />}
        iconClassName="bg-brand-tint text-brand-deep-ink"
        title={t("devices.terminalModal.accountsTitle")}
        description={t("devices.terminalModal.accountsDesc")}
        shortDescription={t("devices.terminalModal.accountsDescShort")}
        isNarrow={isNarrow}
      />
      <div className={`flex flex-col ${isNarrow ? "gap-3.5 p-4" : "gap-3 px-4 py-3"}`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className={EYEBROW_CLASS}>{t("devices.posModal.accountsLabel")}</span>
          <span className="rounded-full bg-brand-tint px-[9px] py-[3px] text-[11px] font-bold text-brand-deep-ink">
            {t("devices.posModal.selectedPill", { count: chosenCount })}
          </span>
        </div>

        <DeviceAccountList
          accounts={accounts}
          selectedIds={selectedSet}
          onChange={onSelectedChange}
          isNarrow={isNarrow}
          // По-нисък списък — място за бележката за заетите без скрол.
          isDense
        />

        {takenAccounts.length > 0 && (
          <div className="flex items-start gap-2 rounded-[10px] bg-canvas px-3 py-2 text-[11.5px] leading-[1.4] text-body">
            <LockIcon size={14} className="mt-px shrink-0" />
            <span>
              {t("devices.terminalModal.takenNote", {
                count: takenAccounts.length,
                where:
                  takenTerminals.length === 1
                    ? `„${takenTerminals[0]}“`
                    : t("devices.terminalModal.takenWhereMany"),
              })}
            </span>
          </div>
        )}
      </div>
    </DeviceSectionCard>
  );
};

// memo: при писане на името в хедъра секцията не се прерисува.
export default memo(TerminalAccountsSection);
